import React, { Suspense } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { GlobalStateProvider } from './context/GlobalStateContext';
import { VisibilityProvider } from './context/VisibilityContext';
import { HomeworkProvider } from './context/HomeworkContext';
import ErrorBoundary from './components/ErrorBoundary';
import AppContent from './components/AppContent';
import './styles/App.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FaSpinner } from 'react-icons/fa';

// Create LoadingSpinner component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <FaSpinner className="animate-spin h-8 w-8 text-sky-500" />
  </div>
);

// Configure QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Data considered fresh for 5 minutes
      cacheTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
      refetchOnWindowFocus: false, // Don't refetch on window focus
      retry: 1, // Only retry once on failure
    },
  },
});

function App() {
  // Global error handler to catch browser extension errors
  React.useEffect(() => {
    const handleError = (event) => {
      // Suppress errors from browser extensions (content_script.js)
      if (event.error && (
        event.error.message?.includes('content_script.js') ||
        event.error.stack?.includes('content_script.js') ||
        event.filename?.includes('content_script.js') ||
        (event.error.message?.includes('control') && event.error.message?.includes('undefined'))
      )) {
        // Log but don't break the app
        console.warn('Browser extension error caught and suppressed:', event.error);
        event.preventDefault();
        return true;
      }
    };

    const handleUnhandledRejection = (event) => {
      // Suppress unhandled promise rejections from browser extensions
      if (event.reason && (
        event.reason.message?.includes('content_script.js') ||
        event.reason.stack?.includes('content_script.js') ||
        (event.reason.message?.includes('control') && event.reason.message?.includes('undefined'))
      )) {
        console.warn('Browser extension promise rejection caught and suppressed:', event.reason);
        event.preventDefault();
        return true;
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <>
    <div id="tooltip-portal" className="fixed inset-0 pointer-events-none z-50" />

    <ErrorBoundary>
      <GlobalStateProvider>
        <VisibilityProvider>
          <HomeworkProvider>
            <Router>
              <QueryClientProvider client={queryClient}>
                <Suspense fallback={<LoadingSpinner />}>
                  <AppContent />
                </Suspense>
              </QueryClientProvider>
            </Router>
          </HomeworkProvider>
        </VisibilityProvider>
      </GlobalStateProvider>
    </ErrorBoundary>
    </>
  );
}

export default App;